import { forwardRef, useEffect, useMemo, useRef } from "react";
import Select, { SelectProps } from "@/components/atoms/Select";
import { useGetShippingAddresses } from "@/services/Company";
import { useTranslation } from "react-i18next";
import { Address } from "@/types";

interface SelectShippingAddressProps extends SelectProps {
  companyId: number | undefined;
  setSelectedAddress?: (address: Address) => void;
  autoSelectDefault?: boolean;
}

const formatAddressLabel = (address: Address, defaultLabel: string) => {
  const parts = [address.address, address.postCode, address.place].filter(Boolean);
  const base = parts.join(", ");
  return address.isDefault ? `${base} (${defaultLabel})` : base;
};

const SelectShippingAddress = forwardRef<HTMLInputElement, SelectShippingAddressProps>(
  ({ companyId, setSelectedAddress, autoSelectDefault = true, onChange, value, ...restProps }, ref) => {
    const { t } = useTranslation();

    const { data: addresses, isLoading } = useGetShippingAddresses(companyId as number, {
      enabled: !!companyId,
    });

    const sourceData = addresses ?? [];

    const options = useMemo(
      () =>
        sourceData.map((address) => ({
          value: String(address.id),
          label: formatAddressLabel(address, t("settings:default")),
        })),
      [sourceData, t]
    );

    const hasAutoSelectedRef = useRef(false);

    useEffect(() => {
      if (!autoSelectDefault || hasAutoSelectedRef.current || !sourceData.length) return;
      if (value !== undefined && value !== null && value !== "") {
        hasAutoSelectedRef.current = true;
        return;
      }
      const defaultAddress = sourceData.find((a) => a.isDefault) ?? sourceData[0];
      if (!defaultAddress) return;
      const valueStr = String(defaultAddress.id);
      onChange?.(valueStr, { value: valueStr, label: formatAddressLabel(defaultAddress, t("settings:default")) });
      setSelectedAddress?.(defaultAddress);
      hasAutoSelectedRef.current = true;
    }, [autoSelectDefault, sourceData, value, onChange, setSelectedAddress, t]);

    const handleChange = (val: string | null, option: any) => {
      onChange?.(val, option);
      if (val != null && setSelectedAddress) {
        const found = sourceData.find((a) => String(a.id) === val);
        if (found) setSelectedAddress(found);
      }
    };

    return (
      <Select
        ref={ref}
        data={options}
        loading={isLoading}
        value={value ?? undefined}
        onChange={handleChange}
        comboboxProps={{ withinPortal: true, zIndex: 10001 }}
        {...restProps}
      />
    );
  }
);

SelectShippingAddress.displayName = "SelectShippingAddress";

export default SelectShippingAddress;
